import React, { useContext, useState, useEffect } from 'react';
import { Context } from '../../contextes/ModalContext';
import { useTheme } from '../../contextes/ThemeContext';
import {
  images,
  icons,
  getDecimalsByTokenSymbol,
  SUPPORTED_TOKENS,
  SupportedTokenSymbol,
  TokenBalance,
} from '../../shared/constants';
import Button from '../../UI/Button/Button';
import WarningMessage from '../../UI/warning-message/warning-message';
import Loader from '../../UI/loader/Loader';
import './_subscription-modal.scss';
import { useSubscriptionStore } from '../../store/subscriptionStore';
import { useAuthStore } from '../../store/authStore';
import { openStripeInNewTab } from '../../services/stripeRedirect';
import { toast, ToastType } from '../../services/toastService';
import {
  SubscriptionTimeInterval,
  WriterSubscriptionDetails,
} from 'src/declarations/Subscription/Subscription.did';
import {
  getPriceBetweenTokens,
  truncateToDecimalPlace,
} from '../../shared/utils';

interface SubscriptionModalProps {
  handle: string;
  authorPrincipalId: string;
  profileImage: string;
  isPublication: boolean;
  onSubscriptionComplete: () => void;
}

const SubscriptionModal: React.FC<SubscriptionModalProps> = ({
  handle,
  profileImage,
  isPublication,
  onSubscriptionComplete,
  authorPrincipalId,
}) => {
  const modalContext = useContext(Context);
  const darkTheme = useTheme();

  const [selectedOption, setSelectedOption] = useState<string>('');
  const [termsChecked, setTermsChecked] = useState<boolean>(false);
  const [termCheckWarning, setTermCheckWarning] = useState<boolean>(false);
  const [isSubscriptionComplete, setIsSubscriptionComplete] =
    useState<boolean>(false);
  const [sufficientBalance, setSufficientBalance] = useState<boolean>(false);
  const [conversionPrices, setConversionPrices] = useState<{
    [key: string]: { icp: string; ckBTC: string };
  }>({
    Weekly: { icp: '0 ICP', ckBTC: '0 ckBTC' },
    Monthly: { icp: '0 ICP', ckBTC: '0 ckBTC' },
    Annually: { icp: '0 ICP', ckBTC: '0 ckBTC' },
    Lifetime: { icp: '0 ICP', ckBTC: '0 ckBTC' },
  });

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [subscriptionError, setSubscriptionError] = useState<string | null>(
    null
  );
  const { userWallet, tokenBalances, fetchTokenBalances, tokenPrices, restrictedTokenBalance } =
    useAuthStore((state) => ({
      userWallet: state.userWallet,
      tokenBalances: state.tokenBalances,
      fetchTokenBalances: state.fetchTokenBalances,
      tokenPrices: state.tokenPrices,
      restrictedTokenBalance: state.restrictedTokenBalance,
    }));

  const {
    subscribeWriter,
    subscribeWriterWithStripe,
    getWriterSubscriptionDetailsByPrincipalId,
  } = useSubscriptionStore((state) => ({
    subscribeWriter: state.subscribeWriter,
    subscribeWriterWithStripe: state.subscribeWriterWithStripe,
    getWriterSubscriptionDetailsByPrincipalId:
      state.getWriterSubscriptionDetailsByPrincipalId,
  }));

  // 'nua' = pay with NUA tokens (on-chain), 'stripe' = pay with card via Stripe
  const [paymentMethod, setPaymentMethod] = useState<'nua' | 'stripe'>('nua');
  const [selectedStripeOption, setSelectedStripeOption] = useState<string>('');


  const getSufficientBalance = (fee: number) => {
    fee = fee * 1e8;

    if (!termsChecked || !selectedOption) {
      return true;
    }

    var selectedCurrencyAndBalance: TokenBalance = {
      balance: 0,
      token: SUPPORTED_TOKENS[0],
    };
    tokenBalances.forEach((tokenBalance) => {
      if (tokenBalance.token.symbol === 'NUA') {
        selectedCurrencyAndBalance = {
          balance: tokenBalance.balance + restrictedTokenBalance,
          token: tokenBalance.token,
        }
      }
    });

    return (
      selectedCurrencyAndBalance &&
      selectedCurrencyAndBalance.balance >= fee + SUPPORTED_TOKENS[0].fee
    );
  };

  const [subscriptionDetails, setSubscriptionDetails] =
    useState<WriterSubscriptionDetails | null>(null);
  const [optionsLoading, setOptionsLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchSubscriptionDetails = async () => {
      try {
        const details = await getWriterSubscriptionDetailsByPrincipalId(
          authorPrincipalId
        );

        if (details) {
          // Convert fees from e8s to the appropriate units
          const convertFromE8s = (fee: number | undefined) =>
            fee ? fee / 1e8 : 0;

          const convertedDetails = {
            ...details,
            weeklyFee:
              details.weeklyFee.length > 0
                ? [convertFromE8s(Number(details.weeklyFee[0]))]
                : [],
            monthlyFee:
              details.monthlyFee.length > 0
                ? [convertFromE8s(Number(details.monthlyFee[0]))]
                : [],
            annuallyFee:
              details.annuallyFee.length > 0
                ? [convertFromE8s(Number(details.annuallyFee[0]))]
                : [],
            lifeTimeFee:
              details.lifeTimeFee.length > 0
                ? [convertFromE8s(Number(details.lifeTimeFee[0]))]
                : [],
          };

          setSubscriptionDetails(convertedDetails as WriterSubscriptionDetails);
        }
      } catch (error) {
        console.error('Error fetching subscription details:', error);
      } finally {
        setOptionsLoading(false);
      }
    };

    if (authorPrincipalId) {
      fetchSubscriptionDetails();
    }
  }, [authorPrincipalId]);

  useEffect(() => {
    //balance check
    setSufficientBalance(getSufficientBalance(parseFee(selectedOption)[0]));
  }, [selectedOption, termsChecked, tokenBalances]);

  const parseFee = (option: string) => {
    if (!subscriptionDetails) return [0];
    switch (option) {
      case 'Weekly':
        return subscriptionDetails.weeklyFee.length === 0
          ? [0]
          : [Number(subscriptionDetails.weeklyFee[0])];
      case 'Monthly':
        return subscriptionDetails.monthlyFee.length === 0
          ? [0]
          : [Number(subscriptionDetails.monthlyFee[0])];
      case 'Annually':
        return subscriptionDetails.annuallyFee.length === 0
          ? [0]
          : [Number(subscriptionDetails.annuallyFee[0])];
      case 'Lifetime':
        return subscriptionDetails.lifeTimeFee.length === 0
          ? [0]
          : [Number(subscriptionDetails.lifeTimeFee[0])];
      default:
        return [0];
    }
  };

  const updateConversionPrice = (
    option: string,
    tokenSymbol: SupportedTokenSymbol,
    conversionSetter: Function
  ) => {
    const fee = parseFee(option)[0];
    const pricePerUnit =
      getPriceBetweenTokens(
        tokenPrices,
        'NUA',
        tokenSymbol,
        fee * Math.pow(10, getDecimalsByTokenSymbol('NUA'))
      ) / Math.pow(10, getDecimalsByTokenSymbol(tokenSymbol));

    const formattedPrice =
      truncateToDecimalPlace(pricePerUnit, 4) + ` ${tokenSymbol}`;
    conversionSetter((prevPrices: any) => ({
      ...prevPrices,
      [option]: {
        ...prevPrices[option],
        [tokenSymbol.toLowerCase()]: formattedPrice,
      },
    }));
  };

  useEffect(() => {
    if (subscriptionDetails) {
      ['Weekly', 'Monthly', 'Annually', 'Lifetime'].forEach((option) => {
        updateConversionPrice(option, 'ICP', setConversionPrices);
        updateConversionPrice(option, 'ckBTC', setConversionPrices);
      });
    }
  }, [authorPrincipalId, subscriptionDetails]);

  const handleSubscription = async (fee: number[]) => {
    if (!termsChecked || !selectedOption) {
      setTermCheckWarning(true);
      return;
    }

    if (!sufficientBalance) {
      setSubscriptionError('Insufficient balance. Please top up your account.');
      return;
    }

    console.log(
      'Subscribing to: ',
      handle,
      selectedOption,
      authorPrincipalId,
      isPublication
    );
    console.log('Subscribing with option: ', selectedOption);

    const subscriptionInterval: SubscriptionTimeInterval = (() => {
      switch (selectedOption) {
        case 'Weekly':
          return { Weekly: null };
        case 'Monthly':
          return { Monthly: null };
        case 'Annually':
          return { Annually: null };
        case 'Lifetime':
          return { LifeTime: null };
        default:
          return { Weekly: null };
      }
    })();

    setIsLoading(true);
    const result = await subscribeWriter(
      authorPrincipalId,
      subscriptionInterval,
      fee[0] * 1e8
    );
    if (result) {
      setIsSubscriptionComplete(true);
      onSubscriptionComplete();
    } else {
      console.error('Subscription error:', result);
      setSubscriptionError(
        'An error occurred during subscription. Please try again.'
      ); // Set the error message
    }
    setIsLoading(false);
  };

  const closeSubscriptionSuccess = () => {
    modalContext?.closeModal();
    onSubscriptionComplete();
    setIsSubscriptionComplete(false);
  };

  const getSubscriptionPeriodText = (option: string) => {
    switch (option) {
      case 'Weekly':
        return 'week';
      case 'Monthly':
        return 'month';
      case 'Annually':
        return 'year';
      case 'Lifetime':
        return 'lifetime';
      default:
        return 'period';
    }
  };

  const subscriptionOptions = [
    { label: 'Weekly', fee: subscriptionDetails?.weeklyFee },
    { label: 'Monthly', fee: subscriptionDetails?.monthlyFee },
    { label: 'Annually', fee: subscriptionDetails?.annuallyFee },
    { label: 'Lifetime', fee: subscriptionDetails?.lifeTimeFee },
  ];

  const hasValidOptions =
    !optionsLoading &&
    subscriptionOptions.some((option) => option.fee && option.fee.length > 0);

  // Stripe card payment tiers, derived from the writer's stored stripePricing.
  // LifeTime is filtered out: Stripe has no one-time "lifetime" concept and we
  // no longer let writers create LifeTime Stripe prices. Any legacy LifeTime
  // entry on the canister is hidden so it can't be checked out as a yearly
  // recurring subscription.
  const stripePriceTiers = (subscriptionDetails?.stripePricing || [])
    .map(([interval, priceId, cents]) => ({
      label: Object.keys(interval)[0], // 'Weekly' | 'Monthly' | 'Annually'
      priceId,
      usd: (Number(cents) / 100).toFixed(2),
    }))
    .filter((tier) => tier.label !== 'LifeTime');
  const stripeAvailable =
    !optionsLoading &&
    !!subscriptionDetails?.stripeIsActive &&
    stripePriceTiers.length > 0;

  // default to whichever method the writer actually offers
  useEffect(() => {
    if (!hasValidOptions && stripeAvailable) {
      setPaymentMethod('stripe');
    } else {
      setPaymentMethod('nua');
    }
  }, [optionsLoading, subscriptionDetails]);

  const handleStripeSubscription = async () => {
    if (!termsChecked || !selectedStripeOption) {
      setTermCheckWarning(true);
      return;
    }
    const tier = stripePriceTiers.find((t) => t.label === selectedStripeOption);
    const readerId = userWallet?.principal;
    if (!tier || !readerId) {
      setSubscriptionError('Could not start Stripe checkout. Please try again.');
      return;
    }
    setIsLoading(true);
    // open Stripe's hosted checkout in a new tab so the reader keeps their place
    const opened = await openStripeInNewTab(() =>
      subscribeWriterWithStripe(tier.priceId, authorPrincipalId, readerId)
    );
    setIsLoading(false);
    if (opened) {
      toast('Continue in the new Stripe tab to complete payment…', ToastType.Plain);
      modalContext?.closeModal();
    } else {
      setSubscriptionError('Could not start Stripe checkout. Please try again.');
    }
  };

  const stripePeriodText = (label: string) => {
    switch (label) {
      case 'Weekly':
        return 'week';
      case 'Monthly':
        return 'month';
      case 'Annually':
        return 'year';
      default:
        return 'period';
    }
  };

  return (
    <div
      className={darkTheme ? 'subscription-modal dark' : 'subscription-modal'}
    >
      {isSubscriptionComplete ? (
        <>
          <div className='modal-top-row'>
            <img
              src={darkTheme ? images.NUANCE_LOGO : images.NUANCE_LOGO_BLACK}
              alt='logo'
              className='nuance-logo-subscription'
            />
            <div
              className='subscription-exit-icon'
              onClick={modalContext?.closeModal}
            >
              <img
                src={
                  darkTheme
                    ? icons.EXIT_NOTIFICATIONS_DARK
                    : icons.EXIT_NOTIFICATIONS
                }
                alt='Close modal'
              />
            </div>
          </div>
          <h2 className='subscription-header'>Yes!</h2>
          <div className='subscription-success-info'>
            <img
              className='success-icon'
              src={icons.GRADIENT_PUBLICATION_SUCCESS_ICON}
              alt='success'
            />
          </div>
          <div className='subscription-success-content'>
            <p className='subscription-success-info'>
              You now support <strong>@{handle}</strong>.
              <br />
              You have paid a {selectedOption} fee. You have unlimited access to
              all membership-only content for a{' '}
              {getSubscriptionPeriodText(selectedOption)}.
              <br />
              You can stop your support at any time.
            </p>
          </div>
          <div className='subscription-buttons'>
            <Button
              type='button'
              styleType={{dark: 'navy-dark', light: 'navy'}}
              style={{ padding: '0px 16px', margin: '0px' }}
              onClick={() => modalContext?.closeModal()}
            >
              OK!
            </Button>
            <Button
              type='button'
              styleType={{dark: 'white', light: 'white'}}
              style={{ padding: '0px 16px' }}
              onClick={() => {
                modalContext?.openModal('cancelSubscription');
              }}
            >
              Stop supporting
            </Button>
          </div>
        </>
      ) : termCheckWarning ? (
        <>
          <div className='modal-top-row'>
            <img
              src={darkTheme ? images.NUANCE_LOGO : images.NUANCE_LOGO_BLACK}
              alt='logo'
              className='nuance-logo-subscription'
            />
            <div
              className='subscription-exit-icon'
              onClick={modalContext?.closeModal}
            >
              <img
                src={
                  darkTheme
                    ? icons.EXIT_NOTIFICATIONS_DARK
                    : icons.EXIT_NOTIFICATIONS
                }
                alt='Close modal'
              />
            </div>
          </div>
          <h2 className='subscription-header'>Error</h2>
          <div className='subscription-modal-content'>
            <p className='subscription-info'>
              An error occurred while processing your support. Please try
              again.
              <br />
              {subscriptionError}
            </p>
          </div>
          <div className='subscription-buttons'>
            <Button
              type='button'
              styleType={{dark: 'white', light: 'white'}}
              style={{ padding: '0px 16px', margin: '0px' }}
              onClick={() => modalContext?.closeModal()}
            >
              Cancel
            </Button>
            <Button
              type='button'
              styleType={{dark: 'navy-dark', light: 'navy'}}
              style={{ padding: '0px 16px' }}
              onClick={() => setTermCheckWarning(false)}
            >
              Retry
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className='modal-top-row'>
            <img
              src={darkTheme ? images.NUANCE_LOGO : images.NUANCE_LOGO_BLACK}
              alt='logo'
              className='nuance-logo-subscription'
            />
            <div
              className='subscription-exit-icon'
              onClick={modalContext?.closeModal}
            >
              <img
                src={
                  darkTheme
                    ? icons.EXIT_NOTIFICATIONS_DARK
                    : icons.EXIT_NOTIFICATIONS
                }
                alt='Close subscriptions modal'
              />
            </div>
          </div>
          <h2 className='subscription-header'>
            Support {isPublication ? 'Publication' : 'User'}
          </h2>
          <div className='subscribee-info'>
            <img
              className='profile-image'
              src={profileImage != '' ? profileImage : images.DEFAULT_AVATAR}
              alt='profile'
            />
            {isPublication && (
              <img
                src={icons.PUBLICATION_ICON}
                alt='publication-icon'
                className='subscription-publication-icon'
              />
            )}
            <div className='handle'>
              <p>"{handle}"</p>
            </div>
          </div>

          {hasValidOptions && stripeAvailable && (
            <div className='subscription-method-tabs'>
              <button
                type='button'
                className={`subscription-method-tab ${
                  paymentMethod === 'nua' ? 'active' : ''
                } ${darkTheme ? 'dark' : ''}`}
                onClick={() => {
                  setPaymentMethod('nua');
                  setTermCheckWarning(false);
                }}
              >
                Pay with NUA
              </button>
              <button
                type='button'
                className={`subscription-method-tab ${
                  paymentMethod === 'stripe' ? 'active' : ''
                } ${darkTheme ? 'dark' : ''}`}
                onClick={() => {
                  setPaymentMethod('stripe');
                  setTermCheckWarning(false);
                }}
              >
                Pay with card
              </button>
            </div>
          )}

          <div className='subscription-modal-content'>
            {optionsLoading ? (
              <Loader />
            ) : !hasValidOptions && !stripeAvailable ? (
              <p className='no-subscription-info'>
                Please check back later. The author has not set up any
                subscriptions yet.
              </p>
            ) : paymentMethod === 'stripe' && stripeAvailable ? (
              <>
                <p className='subscription-info'>
                  When you support this{' '}
                  {isPublication ? 'publication' : 'user'} you get unlimited
                  access to all of their membership content, paid by card via
                  Stripe. You can manage or cancel anytime from the billing
                  portal.
                </p>
                <p className='option-label'>
                  Please choose the duration of your membership:
                </p>
                <div className='subscription-options'>
                  {stripePriceTiers.map((tier) => (
                    <div
                      className={`option-wrapper ${
                        selectedStripeOption === tier.label ? 'selected' : ''
                      }`}
                      key={tier.label}
                      onClick={() => setSelectedStripeOption(tier.label)}
                    >
                      <div
                        className={`option ${
                          selectedStripeOption === tier.label ? 'selected' : ''
                        } ${darkTheme ? 'dark' : ''}`}
                      >
                        <div className='option-content'>
                          <img
                            src={
                              selectedStripeOption === tier.label
                                ? icons.GRADIENT_STAR
                                : icons.NO_FILL_STAR
                            }
                            alt='star'
                            className='star-icon'
                          />
                          <div className='option-details'>
                            <p className='option-title'>{tier.label}</p>
                            <p>
                              <strong>${tier.usd} USD</strong>
                            </p>
                            <div
                              className={
                                darkTheme
                                  ? 'subscription-conversions dark'
                                  : 'subscription-conversions'
                              }
                            >
                              <p>per {stripePeriodText(tier.label)}</p>
                            </div>
                          </div>
                          <div className='subscription-radio-wrapper'>
                            <input
                              type='radio'
                              name='subscriptionStripeOption'
                              checked={selectedStripeOption === tier.label}
                              onChange={() =>
                                setSelectedStripeOption(tier.label)
                              }
                              className='option-radio'
                            />
                            <span>Select</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <>
                <p className='subscription-info'>
                  When you support this{' '}
                  {isPublication ? 'publication' : 'user'} you get unlimited
                  access to all of their membership content for a fee paid in
                  NUA. You pay the fee per period you choose. After this period,
                  you will receive a notification for a possible continuation.
                </p>
                <p className='option-label'>
                  Please choose the duration of your membership:
                </p>
                <div className='subscription-options'>
                  {subscriptionOptions.map(
                    (option) =>
                      option.fee &&
                      option.fee.length > 0 && (
                        <div
                          className={`option-wrapper ${selectedOption === option.label ? 'selected' : ''
                            }`}
                          key={option.label}
                          onClick={() => setSelectedOption(option.label)}
                        >
                          <div
                            className={`option ${selectedOption === option.label ? 'selected' : ''
                              } ${darkTheme ? 'dark' : ''}`}
                          >
                            <div className='option-content'>
                              <img
                                src={
                                  selectedOption === option.label
                                    ? icons.GRADIENT_STAR
                                    : icons.NO_FILL_STAR
                                }
                                alt='star'
                                className='star-icon'
                              />
                              <div className='option-details'>
                                <p className='option-title'>{option.label}</p>
                                <p>
                                  <strong>{option.fee} NUA</strong>
                                </p>
                                <div
                                  className={
                                    darkTheme
                                      ? 'subscription-conversions dark'
                                      : 'subscription-conversions'
                                  }
                                >
                                  <p>= {conversionPrices[option.label]?.icp}</p>
                                  <p>
                                    = {conversionPrices[option.label]?.ckBTC}
                                  </p>
                                  <p>
                                    ={' '}
                                    {truncateToDecimalPlace(
                                      getPriceBetweenTokens(
                                        tokenPrices,
                                        'NUA',
                                        'ckUSDC',
                                        parseFloat(option.fee[0] || '0') *
                                        Math.pow(
                                          10,
                                          getDecimalsByTokenSymbol('NUA')
                                        )
                                      ) /
                                      Math.pow(
                                        10,
                                        getDecimalsByTokenSymbol('NUA')
                                      ),
                                      2
                                    )}{' '}
                                    USD
                                  </p>
                                </div>
                              </div>
                              <div className='subscription-radio-wrapper'>
                                <input
                                  type='radio'
                                  name='subscriptionOption'
                                  checked={selectedOption === option.label}
                                  onChange={() =>
                                    setSelectedOption(option.label)
                                  }
                                  className='option-radio'
                                />
                                <span>Select</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                  )}
                </div>
              </>
            )}
          </div>
          <div className='subscription-modal-footer'>
            {(hasValidOptions || stripeAvailable) && (
              <div className='subscription-terms'>
                <label className='terms-label'>
                  <input
                    type='checkbox'
                    className='checkbox'
                    checked={termsChecked}
                    onChange={() => {
                      setTermsChecked(!termsChecked);
                      setTermCheckWarning(false);
                    }}
                  />
                  <span className='terms'>
                    I am aware of{' '}
                    <a
                      style={darkTheme ? { color: 'white' } : {}}
                      href='https://app.gitbook.com/o/-McG0wq9TbYHdM2GDu8k/s/-MfI7efMoHhyGJ3oojln/terms-and-conditions'
                      target='_blank'
                      rel='noreferrer'
                      onClick={(e) => e.stopPropagation()} // Prevent the link click from toggling the checkbox
                    >
                      terms and conditions
                    </a>
                    , general policy and agree to them.
                  </span>
                </label>
              </div>
            )}

            {termCheckWarning && (
              <WarningMessage message='Please select an option and agree to the terms and conditions.' />
            )}

            {paymentMethod === 'nua' && !sufficientBalance && !optionsLoading && (
              <WarningMessage
                message='Insufficient balance. Please fund your '
                link='/my-profile/wallet'
                onClick={() => modalContext?.closeModal()}
              />
            )}
            <div className='subscription-buttons'>
              <Button
                type='button'
                styleType={{dark: 'white', light: 'white'}}
                style={{ padding: '0px 16px', margin: '0px' }}
                onClick={() => modalContext?.closeModal()}
              >
                Cancel
              </Button>
              {paymentMethod === 'stripe' ? (
                <Button
                  type='button'
                  styleType={{ dark: 'navy-dark', light: 'navy' }}
                  style={{
                    padding: '0px 16px',
                    display: 'flex',
                    flexDirection: 'row-reverse',
                  }}
                  loading={isLoading}
                  disabled={!termsChecked || !selectedStripeOption || isLoading}
                  onClick={handleStripeSubscription}
                >
                  Continue to payment
                </Button>
              ) : (
                <Button
                  type='button'
                  styleType={{dark: 'navy-dark', light: 'navy'}}
                  style={{
                    padding: '0px 16px',
                    display: 'flex',
                    flexDirection: 'row-reverse',
                  }}
                  loading={isLoading}
                  disabled={
                    !termsChecked ||
                    !selectedOption ||
                    isLoading ||
                    !sufficientBalance
                  }
                  onClick={() => handleSubscription(parseFee(selectedOption))}
                >
                  Support
                </Button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SubscriptionModal;
